import { useSyncExternalStore } from 'react';

/**
 * firstRunHint — the tiny early read that lets a brand-new user go STRAIGHT
 * to their welcome (founder, 2026-09-30: "we display the dashboard first and
 * like 1 sec after, the welcome modal"). The owner's setup guide used to wait
 * for the WHOLE dashboard load before it could decide to open; a new
 * member's role welcome waited for its own membership read after mount.
 *
 * `prefetchFirstRunHint(tenantId)` is kicked off (fire-and-forget) by the
 * auth tenant loader while the splash is still up, reading — in parallel —
 * only what the two welcome gates need:
 *   - `tenants.default_admin_user_id, onboarding_status, name` (the caller's
 *     OWN tenant row, already readable by every member under RLS — the same
 *     row, and the same `name` column, the auth loader and the dashboard read),
 *   - the caller's OWN `tenant_memberships.created_at` (own-row RLS).
 * The gates read it synchronously on their first render, so the decision is
 * made before the dashboard paints.
 *
 * Safety: in-memory only (never persisted), keyed by user + tenant so another
 * account on the same device can never see it, holds ids/timestamps/UX flags
 * plus the caller's OWN company name (KAN-562: so the owner welcome's
 * "{company} is set up" pill is rarely late — no person names, no other
 * tenant's data, dropped at sign-out with the rest). A failed or slow read
 * just leaves the hint unloaded and the gates fall back to their existing
 * (post-load) path — never blocks sign-in.
 */
const EMPTY = { key: null, loaded: false, failed: false, defaultAdminUserId: null, onboardingStatus: null, membershipCreatedAt: null, tenantName: null };
let state = EMPTY;
const listeners = new Set();
let inflightKey = null;

function emit(next) {
  state = next;
  listeners.forEach((fn) => fn());
}

const keyOf = (userId, tenantId) => (userId && tenantId ? `${userId}:${tenantId}` : null);

export async function prefetchFirstRunHint(tenantId) {
  try {
    if (!tenantId) return;
    // Loaded lazily so the sign-out cleanup (which imports resetFirstRunHint)
    // never drags the Supabase client into its own module graph.
    const { supabase } = await import('../../supabase');
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id || null;
    const key = keyOf(userId, tenantId);
    if (!key || key === inflightKey || (state.key === key && state.loaded)) return;
    inflightKey = key;
    const [tenantRes, memberRes] = await Promise.all([
      supabase.from('tenants').select('default_admin_user_id, onboarding_status, name').eq('id', tenantId).maybeSingle(),
      supabase.from('tenant_memberships').select('created_at').eq('tenant_id', tenantId).eq('user_id', userId).maybeSingle(),
    ]);
    if (inflightKey !== key) return; // a newer session superseded this read
    inflightKey = null;
    if (tenantRes.error || memberRes.error) {
      emit({ ...state, key, loaded: true, failed: true });
      return;
    }
    emit({
      key,
      loaded: true,
      failed: false,
      defaultAdminUserId: tenantRes.data?.default_admin_user_id ?? null,
      onboardingStatus: tenantRes.data?.onboarding_status ?? null,
      membershipCreatedAt: memberRes.data?.created_at ?? null,
      tenantName: typeof tenantRes.data?.name === 'string' ? tenantRes.data.name : null,
    });
  } catch (err) {
    inflightKey = null; // a failed read never blocks a later retry
    // Fail quiet: the gates keep their post-load path.
    console.warn('[first-run hint] prefetch failed', err);
  }
}

/**
 * Keep the hint's copy of `onboarding_status.guide` current after a guide
 * write this session (auto_shown_at, step, paused, completed) — so a gate
 * that re-mounts later in the same session never re-decides from the
 * sign-in snapshot and pops the guide again.
 */
export function patchFirstRunHintGuide(userId, tenantId, guide) {
  const key = keyOf(userId, tenantId);
  if (!key || state.key !== key || !state.loaded) return;
  emit({ ...state, onboardingStatus: { ...(state.onboardingStatus || {}), guide: guide ?? null } });
}

// Once-per-session latch for the owner setup guide's AUTOMATIC open (KAN-745).
// Module-level so it survives AdminHome remounts (log-job round trip, iOS
// swipe-back) even when the auto_shown_at stamp write failed. Ids only, never
// persisted, cleared at sign-out via resetFirstRunHint.
const autoHandled = new Set();

export function markSetupGuideAutoHandled(userId, tenantId) {
  const key = keyOf(userId, tenantId);
  if (key) autoHandled.add(key);
}

export function isSetupGuideAutoHandled(userId, tenantId) {
  const key = keyOf(userId, tenantId);
  return key ? autoHandled.has(key) : false;
}

/** Test/sign-out hook — drop the in-memory hint (and the auto-open latch). */
export function resetFirstRunHint() {
  inflightKey = null;
  autoHandled.clear();
  emit(EMPTY);
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * The hint for THIS user + tenant, or an unloaded hint when it belongs to
 * someone else / hasn't arrived. Never trust it for authorization — it only
 * decides WHEN a welcome may open; every write stays server-enforced.
 */
export function useFirstRunHint(userId, tenantId) {
  const snap = useSyncExternalStore(subscribe, () => state, () => state);
  const key = keyOf(userId, tenantId);
  if (!key || snap.key !== key || !snap.loaded || snap.failed) {
    return { loaded: false, defaultAdminUserId: null, onboardingStatus: null, membershipCreatedAt: null, tenantName: null };
  }
  return snap;
}
